// src/pages/account/MessagingPortal.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { dynastyTheme } from '../../services/colorService';
import apiService from '../../services/apiService';
import { formatApiError } from '../../services/errorUtils';
import { AlertTriangle, CheckCircle, Clock, Mail, RefreshCw, Send, XCircle } from 'lucide-react';

/**
 * DD-ADMIN-MESSAGING-0728 — commissioner messaging portal, inside the owner-only Users view.
 *
 * The owner picks a template, picks a recipient or a cohort, edits the prefilled text, and sends.
 * Messages go as the commissioner (Reply-To is the owner's real mailbox), never as a no-reply.
 *
 * FOUR THINGS THIS UI IS CAREFUL ABOUT:
 *   1. The text is ALWAYS editable before sending. A template fills the box; it never bypasses it.
 *   2. A failed send is shown as a failure, per recipient, with the server's reason — never
 *      collapsed into "done" (Rule 9). Results list successes and failures separately.
 *   3. Anyone already contacted is flagged in the picker BEFORE sending, and the server refuses a
 *      duplicate inside the cooldown; the refusal becomes an explicit "send anyway" here rather
 *      than a silent resend.
 *   4. The idle automation renders as an UNCHECKED, disabled control with its real state from the
 *      server. It ships dormant and this must not imply otherwise.
 *
 * Authorization is server-side (verify_platform_owner). This component is only ever mounted
 * inside the owner-gated tab; that is convenience, not security.
 */

const fmt = (iso) => {
    if (!iso) return null;
    const d = new Date(/[Z+]|-\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
};

const MessagingPortal = ({ setMessage }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [templateKey, setTemplateKey] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [mode, setMode] = useState('single');          // 'single' | 'cohort'
    const [recipientId, setRecipientId] = useState('');
    const [cohortKey, setCohortKey] = useState('');
    const [sending, setSending] = useState(false);
    const [results, setResults] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiService.get('/api/account/admin/messaging');
            setData(response.data);
        } catch (err) {
            const msg = formatApiError(err, 'Failed to load the messaging portal');
            setError(msg);
            if (setMessage) setMessage({ text: msg, type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [setMessage]);

    useEffect(() => { load(); }, [load]);

    const recipients = useMemo(
        () => (data?.recipients || []).filter((r) => !r.is_synthetic && r.email),
        [data]
    );

    const applyTemplate = (key) => {
        setTemplateKey(key);
        const t = (data?.templates || []).find((x) => x.template_key === key);
        if (t) {
            // Prefill, do not lock — the owner reviews and edits every send.
            setSubject(t.subject);
            setBody(t.body);
        }
    };

    const cohortMembers = useMemo(() => {
        if (!cohortKey) return [];
        return recipients.filter((r) => (r.cohorts || []).includes(cohortKey));
    }, [recipients, cohortKey]);

    const targets = mode === 'cohort'
        ? cohortMembers
        : recipients.filter((r) => r.user_id === recipientId);

    const sendOne = async (target, allowDuplicate) => {
        const response = await apiService.post('/api/account/admin/messaging/send', {
            recipient_user_id: target.user_id,
            recipient_email: target.email,
            subject,
            body,
            template_key: templateKey || null,
            recipient_name: target.name || null,
            allow_duplicate: allowDuplicate,
        });
        return { ...response.data, target };
    };

    const handleSend = async (allowDuplicate = false) => {
        if (!targets.length) return;
        setSending(true);
        setResults(null);
        const outcomes = [];
        for (const target of targets) {
            try {
                const r = await sendOne(target, allowDuplicate);
                outcomes.push({
                    email: target.email,
                    ok: r.sent,
                    // A 200 with sent:false carries the provider's reason — show it, don't hide it.
                    reason: r.error || null,
                });
            } catch (err) {
                // 400 = refused before sending (duplicate inside the cooldown, empty body…).
                outcomes.push({
                    email: target.email,
                    ok: false,
                    reason: formatApiError(err, 'Send failed'),
                    refused: err?.response?.status === 400,
                });
            }
        }
        setResults(outcomes);
        setSending(false);
        await load();                                    // refresh contact history + send log
    };

    if (loading) {
        return (
            <div className={`${dynastyTheme.components.card.base} p-8 flex items-center gap-3`}>
                <RefreshCw size={20} className="animate-spin text-yellow-400" />
                <span className={dynastyTheme.classes.text.neutralLight}>Loading messaging…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`${dynastyTheme.components.card.base} p-8`}>
                <p className="text-red-400 mb-4">{error}</p>
                <button onClick={load} className={dynastyTheme.utils.getComponent('button', 'primary', 'sm')}>
                    Try again
                </button>
            </div>
        );
    }

    const automation = data?.idle_automation || {};
    const duplicatesInBatch = targets.filter((t) => t.contact);

    return (
        <div className="space-y-6">
            <div className={`${dynastyTheme.components.card.base} p-6`}>
                <div className="flex items-center gap-3 mb-6">
                    <Mail size={22} className="text-yellow-400" />
                    <h3 className={dynastyTheme.components.heading.h3}>Message users</h3>
                </div>

                {/* Template */}
                <label className="block text-sm font-semibold mb-2 text-yellow-400">Template</label>
                <select
                    aria-label="Template"
                    value={templateKey}
                    onChange={(e) => applyTemplate(e.target.value)}
                    className="w-full mb-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                >
                    <option value="">Write from scratch…</option>
                    {(data?.templates || []).map((t) => (
                        <option key={t.template_key} value={t.template_key}>{t.title}</option>
                    ))}
                </select>
                {templateKey && (
                    <p className={`text-xs mb-4 ${dynastyTheme.classes.text.neutralLight}`}>
                        {(data.templates.find((t) => t.template_key === templateKey) || {}).situation}
                    </p>
                )}

                {/* Recipients */}
                <div className="flex gap-2 mb-3 mt-4">
                    <button
                        onClick={() => setMode('single')}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold ${mode === 'single' ? 'bg-yellow-400 text-black' : 'bg-gray-800 text-yellow-400'}`}
                    >
                        One person
                    </button>
                    <button
                        onClick={() => setMode('cohort')}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold ${mode === 'cohort' ? 'bg-yellow-400 text-black' : 'bg-gray-800 text-yellow-400'}`}
                    >
                        Cohort
                    </button>
                </div>

                {mode === 'single' ? (
                    <select
                        aria-label="Recipient"
                        value={recipientId}
                        onChange={(e) => setRecipientId(e.target.value)}
                        className="w-full mb-4 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    >
                        <option value="">Choose a recipient…</option>
                        {recipients.map((r) => (
                            <option key={r.user_id} value={r.user_id}>
                                {r.name || r.email}{r.contact ? ' — already messaged' : ''}
                            </option>
                        ))}
                    </select>
                ) : (
                    <>
                        <select
                            aria-label="Cohort"
                            value={cohortKey}
                            onChange={(e) => setCohortKey(e.target.value)}
                            className="w-full mb-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                        >
                            <option value="">Choose a cohort…</option>
                            {(data?.cohorts || []).map((c) => (
                                <option key={c.key} value={c.key}>{c.key} — {c.description}</option>
                            ))}
                        </select>
                        {cohortKey && (
                            <p className={`text-sm mb-4 ${dynastyTheme.classes.text.neutralLight}`}>
                                {cohortMembers.length} user{cohortMembers.length === 1 ? '' : 's'}:{' '}
                                {cohortMembers.map((r) => r.email).join(', ') || 'nobody right now'}
                            </p>
                        )}
                    </>
                )}

                {/* The editable message */}
                <label className="block text-sm font-semibold mb-2 text-yellow-400">Subject</label>
                <input
                    aria-label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full mb-4 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                />
                <label className="block text-sm font-semibold mb-2 text-yellow-400">Message</label>
                <textarea
                    aria-label="Message"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={12}
                    className="w-full mb-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white font-mono text-sm"
                />
                <p className={`text-xs mb-4 ${dynastyTheme.classes.text.neutralLight}`}>
                    {'{{first_name}}'} is replaced per recipient. Sent as you — replies come back to
                    your inbox.
                </p>

                {duplicatesInBatch.length > 0 && (
                    <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                        <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-sm text-amber-200">
                            {duplicatesInBatch.length} of these {duplicatesInBatch.length === 1 ? 'has' : 'have'} been
                            messaged before. Sending will be refused unless you choose “Send anyway”.
                        </p>
                    </div>
                )}

                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={() => handleSend(false)}
                        disabled={sending || !targets.length || !subject.trim() || !body.trim()}
                        className={`${dynastyTheme.utils.getComponent('button', 'primary', 'md')} flex items-center gap-2 disabled:opacity-40`}
                    >
                        <Send size={16} />
                        {sending ? 'Sending…' : `Send to ${targets.length || 0}`}
                    </button>
                    {duplicatesInBatch.length > 0 && (
                        <button
                            onClick={() => handleSend(true)}
                            disabled={sending || !targets.length}
                            className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'md')} disabled:opacity-40`}
                        >
                            Send anyway
                        </button>
                    )}
                </div>

                {/* Per-recipient outcome. Failures are never folded into a success summary. */}
                {results && (
                    <div className="mt-6 space-y-2">
                        {results.map((r) => (
                            <div key={r.email} className="flex items-start gap-2 text-sm">
                                {r.ok
                                    ? <CheckCircle size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                                    : <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" />}
                                <span className={r.ok ? 'text-emerald-300' : 'text-red-300'}>
                                    {r.email}{r.ok ? ' — sent' : ` — ${r.refused ? 'not sent' : 'FAILED'}: ${r.reason}`}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* The dormant automation */}
            <div className={`${dynastyTheme.components.card.base} p-6`}>
                <label className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        aria-label="Automatically message idle users"
                        checked={Boolean(automation.enabled)}
                        disabled
                        readOnly
                        className="mt-1"
                    />
                    <span>
                        <span className="font-semibold text-white">
                            Automatically message idle users
                        </span>
                        <span className="ml-2 px-2 py-0.5 text-xs rounded bg-gray-700 text-gray-300">
                            off
                        </span>
                        <span className={`block text-sm mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
                            Sends “{automation.template_key}” to users not seen for{' '}
                            {automation.idle_after_days} days, at most once every{' '}
                            {automation.recontact_cooldown_days} days. {automation.note}
                        </span>
                    </span>
                </label>
            </div>

            {/* Send log — the anti-spam memory, visible */}
            <div className={`${dynastyTheme.components.card.base} p-6`}>
                <div className="flex items-center gap-3 mb-4">
                    <Clock size={20} className="text-yellow-400" />
                    <h3 className={dynastyTheme.components.heading.h3}>Sent</h3>
                </div>
                {(data?.history || []).length === 0 ? (
                    <p className={dynastyTheme.classes.text.neutralLight}>Nothing sent yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className={`${dynastyTheme.classes.text.neutralLight} border-b border-gray-700`}>
                                <tr>
                                    <th className="text-left px-3 py-2 font-semibold">When</th>
                                    <th className="text-left px-3 py-2 font-semibold">To</th>
                                    <th className="text-left px-3 py-2 font-semibold">Subject</th>
                                    <th className="text-left px-3 py-2 font-semibold">Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.history.map((h) => (
                                    <tr key={h.message_id} className="border-b border-gray-800">
                                        <td className={`px-3 py-2 ${dynastyTheme.classes.text.neutralLight}`}>
                                            {fmt(h.created_at) || '—'}
                                        </td>
                                        <td className={`px-3 py-2 ${dynastyTheme.classes.text.neutralLight}`}>
                                            {h.recipient_email}
                                            {h.is_automated && (
                                                <span className="ml-2 px-2 py-0.5 text-xs rounded bg-gray-700 text-gray-300">
                                                    auto
                                                </span>
                                            )}
                                        </td>
                                        <td className={`px-3 py-2 ${dynastyTheme.classes.text.neutralLight}`}>
                                            {h.subject}
                                        </td>
                                        <td className="px-3 py-2">
                                            {h.status === 'sent' ? (
                                                <span className="text-emerald-400">sent</span>
                                            ) : (
                                                <span className="text-red-400" title={h.error_message || ''}>
                                                    failed
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessagingPortal;
